import type { SupportCenterFormKind } from './supportCenterSpecialties'
import type { SupportCenterElegibilidadeEspecialidade } from './supportCenterAgenda'
import type {
  SupportCenterAppointment,
  SupportCenterAppointmentStatus,
  SupportCenterTriageStatus,
} from './supportCenterTriage'

export type { SupportCenterAppointment, SupportCenterAppointmentStatus, SupportCenterFormKind, SupportCenterTriageStatus }

export type SupportCenterAppointmentContactOwner = 'holder' | 'patient'

/** Canais gravados em support_center_appointments.channel. */
export type SupportCenterAppointmentChannel =
  | 'whatsapp'
  | 'telefone'
  | 'videochamada'
  | 'whatsapp-bot'

/** Canais selecionáveis no formulário da Central Amparo (sem bot). */
export type SupportCenterAppointmentFormChannel = 'whatsapp' | 'telefone' | 'videochamada'

/** Canais selecionáveis no cancelamento (POST /agendamentos/{id}/cancelar). */
export type SupportCenterAppointmentCancelChannel = 'whatsapp' | 'telefone' | 'videochamada'

export const SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS: Record<
  SupportCenterAppointmentChannel,
  string
> = {
  whatsapp: 'WhatsApp',
  telefone: 'Telefone',
  videochamada: 'Videochamada',
  'whatsapp-bot': 'WhatsApp Bot',
}

export const SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS: Array<{
  value: SupportCenterAppointmentFormChannel
  label: string
}> = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'videochamada', label: 'Videochamada' },
]

export const SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS: Array<{
  value: SupportCenterAppointmentCancelChannel
  label: string
}> = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'videochamada', label: 'Videochamada' },
]

export const SUPPORT_CENTER_APPOINTMENT_CHANNEL_FILTER_OPTIONS: Array<{
  value: SupportCenterAppointmentChannel
  label: string
}> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'videochamada', label: 'Videochamada' },
  { value: 'whatsapp-bot', label: 'WhatsApp Bot' },
]

export function appointmentChannelLabel(
  channel: string | null | undefined
): string {
  if (!channel?.trim()) return '—'
  const key = channel.trim() as SupportCenterAppointmentChannel
  return SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[key] || key
}

export type SupportCenterAppointmentCreateInput = {
  holder_cpf: string
  holder_name: string
  holder_birth_date?: string | null
  patient_cpf: string
  patient_name: string
  patient_birth_date?: string | null
  patient_is_insured: boolean
  relationship_to_holder?: string | null
  internal_emails: string[]
  /**
   * Um único e-mail do paciente.
   * Nas rotas /centralamparo o campo email_paciente é string, não array.
   */
  patient_email: string
  /** Telefone do formulário — enviado à API em telefone_paciente (a API grava o contato). */
  patient_phone?: string | null
  /**
   * Pessoa de quem o formulário copiou o contato.
   * O POST não usa este campo: e-mail e telefone saem sempre como do paciente.
   */
  contact_owner?: SupportCenterAppointmentContactOwner
  /** Canal de origem do agendamento (telefone, whatsapp ou videochamada neste sistema). */
  channel: SupportCenterAppointmentFormChannel
  specialization_id: string
  /** Código da assistência escolhida no modal — enviado como codigo_assistencia. */
  assistance_code: string
  /** Preferencial: e-mail do profissional (contrato IA-API). */
  professional_email?: string
  /** Fallback: resolve e-mail via RPC antes do POST. */
  professional_user_id?: string
  scheduled_at: string
  triage_id?: string | null
  /**
   * Especialidades + coberturas da GET /elegibilidade (apólice, convênio, fornecedor, corretor, cliente).
   * Obrigatório para montar cod_apolice / cod_convenio / cod_fornecedor_assistencia / cod_corretor / cod_cliente.
   */
  elegibilidadeEspecialidades?: SupportCenterElegibilidadeEspecialidade[]
}

export type SupportCenterAppointmentUpdateInput = SupportCenterAppointmentCreateInput & {
  id: string
}

export type SupportCenterAppointmentListFilters = {
  range_start: string
  range_end: string
  /** Filtro pelo profissional responsável (professional_user_id). */
  professional_user_id?: string
  /** @deprecated Preferir professional_user_id. Escopo legado: contains(internal_emails, [email]). */
  attendant_email?: string
  specialization_id?: string
  statuses?: SupportCenterAppointmentStatus[]
  /** Inclui cancelados (status=cancelado ou soft-delete incompleto marcado/reagendado). */
  include_cancelled?: boolean
}

export type SupportCenterAttendantOption = {
  id: string
  name: string
  email: string
}

export type SupportCenterAgendaEvent = SupportCenterAppointment & {
  professional_user_id?: string | null
  confirmation_requested_at?: string | null
  /** Status da triagem vinculada (quando existir). */
  triage_status?: SupportCenterTriageStatus | null
}

export const EMPTY_APPOINTMENT_FORM = {
  holder_cpf: '',
  holder_name: '',
  holder_birth_date: '',
  patient_cpf: '',
  patient_name: '',
  patient_birth_date: '',
  patient_is_insured: true,
  relationship_to_holder: '',
  internal_emails: [] as string[],
  client_emails: [] as string[],
  patient_phone: '',
  /** Origem do pré-preenchimento. O POST envia patient_email em email_paciente (string). */
  contact_owner: 'holder' as SupportCenterAppointmentContactOwner,
  /** Código da assistência escolhida no passo 2 (elegibilidade). */
  assistance_code: '',
  /** Canal de agendamento (telefone | whatsapp | videochamada). Vazio até o usuário escolher. */
  channel: '' as '' | SupportCenterAppointmentFormChannel,
  specialization_id: '',
  professional_user_id: '',
  scheduled_at: '',
}

export type SupportCenterAppointmentFormState = typeof EMPTY_APPOINTMENT_FORM

/** Anexo gravado no início do atendimento (bucket support-center-appointments). */
export type SupportCenterAppointmentAttachment = {
  id: string
  appointment_id: string
  file_path: string
  file_name: string
  file_size: number | null
  mime_type: string | null
  uploaded_by: string | null
  created_at: string
  deleted_at: string | null
}

export const SUPPORT_CENTER_ATTACHMENT_BUCKET = 'support-center-appointments'
export const SUPPORT_CENTER_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024 // 10 MB
export const SUPPORT_CENTER_ATTACHMENT_MAX_MB = 10

export const SUPPORT_CENTER_ATTACHMENT_ACCEPT =
  '.doc,.docx,.pdf,.jpg,.jpeg,.png,.gif,.webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/jpeg,image/png,image/gif,image/webp'

export const SUPPORT_CENTER_ATTACHMENT_EXTENSIONS = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
] as const

export const SUPPORT_CENTER_ATTACHMENT_MIME_BY_EXT: Record<string, string> = {
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
}
